import CTNCheckBox from '@ctn/ui/components/ctn-checkbox';
import CTNMessage from '@ctn/ui/components/ctn-message';
import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNTextArea from '@ctn/ui/components/ctn-textarea';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import classNames from 'classnames';
import { useEffect, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';

import DocViewer from '@/components/DocViewer';
import { useGlobalContext } from '@/context/global.context';
import useFetchDocument from '@/hooks/useFetchDocument';
import { usePdfViewer } from '@/hooks/usePdfViewer';
import { postRevisionFirma, postEliminacionFirma } from '@/services/tramite.service';

import { ESTADOS_FIRMA } from '@/constants/tramite.constants';
import { notificationsConfig } from '@/helpers/notifications';
import { getIconDocumento } from '@/helpers/upload';
import { type IDocumento, type IProtocolo } from '@/interfaces/model/don.interface';

import { defaultValues as defaultValuesGlobal, getSchema, type Form } from './form';
import DatosFirma from '../componentes/datosFirma';

type ModalParams = {
	readonly idTramite: string;
	readonly protocolo: IProtocolo;
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onSubmit: () => void;
};

const ModalRevisionFirma = ({
	idTramite,
	protocolo,
	isOpen,
	onClose,
	onSubmit: callbackSubmit,
}: ModalParams): ReactElement => {
	const { openNotification, closeNotification } = useNotification();
	const [solicitarRevision, setSolicitarRevision] = useState<boolean>(false);
	const [solicitarEliminacion, setSolicitarEliminacion] = useState<boolean>(false);
	const notificationTemplate = notificationsConfig({ closeNotification });
	const estadoQuePermiteEliminar = [ESTADOS_FIRMA.PENDIENTE].includes(protocolo.subsanacionFirma?.estado.id ?? 0);
	const seHaPasadoElPlazo = !protocolo.permiteSubsanacionFirma && !estadoQuePermiteEliminar;

	const { user } = useGlobalContext();
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { errors },
	} = useForm<Form>({
		defaultValues: {...defaultValuesGlobal, 
			observacionesPeticion: protocolo.subsanacionFirma?.observacionesPeticion ?? '',
			observacionesSubsanacion: protocolo.subsanacionFirma?.observacionesSubsanacion ?? ''
		},
		resolver: getSchema() as Resolver<Form>,
		mode: 'onChange',
	});

	useEffect(() => {
		reset({...defaultValuesGlobal, 
			observacionesPeticion: protocolo.subsanacionFirma?.observacionesPeticion ?? '',
			observacionesSubsanacion: protocolo.subsanacionFirma?.observacionesSubsanacion ?? ''
		});
	}, [protocolo]);

	const observaciones = watch('observaciones');
	const observacionesPeticion = watch('observacionesPeticion');
	const observacionesSubsanacion = watch('observacionesSubsanacion');
	const observacionesEliminacion = watch('observacionesEliminacion');
	const isWrongFirma = watch('isWrongFirma');	
	const eliminarFirma = watch('eliminarFirma');
	const documento = protocolo.copiaElectronica;
	const { fetchDocument } = useFetchDocument({ idTramite: Number(idTramite) });
	const { isLoading, url, loadPdf } = usePdfViewer();

	const getPdfSource = async (doc: IDocumento) => {
		const id = doc?.id;
		if (!id) return undefined;

		return (await fetchDocument(id))?.data;
	};

	useEffect(() => {
		loadPdf(() => getPdfSource(documento));
	}, []);

	useEffect(() => {
		setSolicitarRevision(isWrongFirma.length === 1 && isWrongFirma[0] ===  '1');
	}, [isWrongFirma]);

	useEffect(() => {
		setSolicitarEliminacion(eliminarFirma.length === 1 && eliminarFirma[0] ===  '1');
	}, [eliminarFirma]);

	const clearForm = () => {
		setValue('isWrongFirma', []);
		setValue('observaciones', '');
		setValue('eliminarFirma', []);
		setValue('observacionesEliminacion', '');
	};

	const onSubmit = async () => {
		const result = await postRevisionFirma({
			idTramite, 
			idFirma: protocolo.id, 
			motivo: observaciones.trim(), 
			nifEmisor: user?.nifEmisor, 
			tipoSector: user?.tipoSectorEmisor
		});
	
		if (result.code !== 200) {
			openNotification(
				notificationTemplate.errorMessage(
					<>
						<p>
							<b>Se ha producido un error en el envió de la solicitud para la revisión de la escritura.</b>
						</p>
						<p>
							Por favor, vuelva a intentarlo
						</p>
					</>
				),
			);
			return;
		}

		openNotification(
			notificationTemplate.successMessage(
				'Revisión enviada',
				'La solicitud para la revisión de la escritura se ha enviado correctamente.',
			),
		);
		
		clearForm();
		callbackSubmit();
	};

	const onSubmitEliminacion = async () => {
		const result = await postEliminacionFirma({
			idTramite, 
			idFirma: protocolo.id, 
			idSubsanacionFirma: protocolo.subsanacionFirma?.id ?? 0,
			motivo: observacionesEliminacion.trim(), 
			nifEmisor: user?.nifEmisor, 
			tipoSector: user?.tipoSectorEmisor
		});

		if (result.code !== 200) {
			openNotification(
				notificationTemplate.errorMessage(
					<>
						<p>
							<b>Se ha producido un error al cancelar el envió de la solicitud para la revisión de la firma</b>
						</p>
						<p>
							Por favor, vuelva a intentarlo
						</p>
					</>
				),
			);
			return;
		}

		openNotification(
			notificationTemplate.successMessage(
				'Revisión cancelada',
				'La solicitud para la revisión de la firma se ha cancelado correctamente',
			),
		);

		clearForm();
		callbackSubmit();
	};

	const handleClose = () => {
		onClose();
		clearForm();
	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'cancel',
			mode: 'tertiary',
			label: 'Cerrar',
			size: 'size-40',
			minWidth: '140',
			disabled: false,
			onClick: handleClose,
		},
	];

	if (solicitarRevision) {
		actionButtons.push({
			key: 'enviar',
			disabled: !observaciones?.trim() || Boolean(errors.observaciones),
			mode: 'primary',
			label: 'Enviar solicitud',
			minWidth: '140',
			size: 'size-40',
			loading: false,
			onClick(e) { 
				handleSubmit(onSubmit)(e);
			},
		});
		actionButtons[0].label = 'Cancelar';
	} else if (solicitarEliminacion) {
		actionButtons.push({
			key: 'eliminarSolicitud',
			disabled: !observacionesEliminacion?.trim() || Boolean(errors.observacionesEliminacion),
			mode: 'danger',
			label: 'Eliminar solicitud',
			minWidth: '140',
			size: 'size-40',
			loading: false,
			onClick(e) {
				handleSubmit(onSubmitEliminacion)(e);
			},
		});
		actionButtons[0].label = 'Cancelar';
	}
	
	return (
		<CTNModal
			autoHeight
			closeable
			className={classNames('c-pnp-modal', 'c-pnp-party-amendment')}
			type="basic"
			title='Detalle de la información de la escritura'
			size="xxl"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<>
					{seHaPasadoElPlazo && (
						<CTNMessage
							content={
								<p>
									Se ha superado el plazo límite permitido para pedir la revisión de esta escritura. Si necesita realizar cualquier consulta, contacte directamente con la notaría.
								</p>
							}
							icon="info"
							status="info"
							mode="outline"
							closable={false}
						/>
					)}
					<section className={`[ c-pnp-form-group__datos-firma ${protocolo.permiteSubsanacionFirma ? '' : 'u-mt-16' }`}>
						<DatosFirma
							hasAcciones={false}
							protocolo={protocolo}
						/>
					</section>
					<DocViewer
						mimeType={documento.mimeType.nombre}
						loading={isLoading}
						url={url}
						fileIcon={getIconDocumento(documento)}
						fileName={documento.nombre}
						view="FitH"
					/>
					{!seHaPasadoElPlazo && (
						<>
							{!estadoQuePermiteEliminar && (
								<>
									{observacionesPeticion && (<CTNTextArea
										required
										displayOnly
										classNameField="[ c-pnp-form-group--paddingless ] [ u-mt-16 ]"
										name="observacionesPeticion"
										label="Motivo de la solicitud de revisión"
										control={control}
									/>)}
									{observacionesSubsanacion && (<CTNTextArea
										required
										displayOnly
										classNameField="[ c-pnp-form-group--paddingless ] [ u-mt-16 ]"
										name="observacionesSubsanacion"
										label="Observaciones notaría"
										control={control}
									/>)}
									<CTNCheckBox
										classNameField="[ c-pnp-privacy-policy__checkbox ] [ u-mt-16 ]"
										name="isWrongFirma"
										items={[
											{
												label: 'La escritura no es correcta y deseo enviar una solicitud de revisión',
												value: '1',
											},
										]}
										noFeedBack={false}
										control={control}/>
								</>
							)}

							{solicitarRevision && (
								<>
									<CTNMessage
										content={
											<p>
												El notario podrá aplicar las modificaciones, rechazar la solicitud si todo está en orden o descartar la documentación.
											</p>
										}
										className="u-mt-16"
										icon="warning"
										status="warning"
										mode="outline"
										closable={false}
									/>
									<CTNTextArea
										required
										classNameField="u-mt-16"
										name="observaciones"
										label="Motivo de la solicitud de revisión"
										control={control}
										maxLength={2000}
										error={errors.observaciones?.message}
										placeholder="Indicar aquí las observaciones."
										message={`${observaciones?.length}/2000`}
									/>
								</>
							)}

							{estadoQuePermiteEliminar && (
								<section className="[ c-pnp-form-group__datos-firma-eliminacion ][ u-mt-16 ]">
									<CTNTextArea
										required
										displayOnly
										classNameField="[ c-pnp-form-group--paddingless ] [ u-mt-16 ]"
										name="observacionesPeticion"
										label="Motivo de la solicitud de revisión"
										control={control}
									/>
									<CTNCheckBox
										classNameField="[ c-pnp-privacy-policy__checkbox ] [ u-mt-16 ]"
										name="eliminarFirma"
										items={[
											{
												label: 'Deseo eliminar la solicitud de revisión de la escritura',
												value: '1',
											},
										]}
										noFeedBack={false}
										control={control}
									/>
									{solicitarEliminacion && (
										<CTNTextArea
											required
											classNameField="u-mt-16"
											name="observacionesEliminacion"
											label="Motivo de la eliminación de la solicitud de revisión"
											control={control}
											maxLength={2000}
											error={errors.observacionesEliminacion?.message}
											placeholder="Indicar aquí las observaciones."
											message={`${observacionesEliminacion?.length}/2000`}
										/>
									)}
								</section>
							)}
						</>
					)}
					
				</>
			}
			onClose={handleClose}
		/>
	);
};

export default ModalRevisionFirma;
